import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The span element that displays the SKU text
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku'];
  #eventTarget;
  #selectionRevision = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#eventTarget = this.dataset.mainProductSku === 'true' ? document : this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#eventTarget = null;
    this.#selectionRevision++;
  }

  /**
   * Handles product select event and updates the SKU.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    const isMainStory = this.dataset.mainProductSku === 'true';
    if (isMainStory) {
      if (!(event.target instanceof Element) || event.target.closest('product-card, quick-add-dialog, dialog, [id*="QuickAdd-"]') || !event.target.closest('[id*="ProductInformation-"]')) return;
      const sourceProductId = event.target.closest('[data-product-id]')?.getAttribute('data-product-id');
      if (sourceProductId && String(sourceProductId).split('/').pop() !== this.dataset.productId) return;
    }
    const revision = ++this.#selectionRevision;
    event.promise
      .then(({ detail }) => {
        if (!detail || revision !== this.#selectionRevision || !this.isConnected) return;

        const { newProduct, resource } = detail;
        if (newProduct && !isMainStory) {
          this.dataset.productId = newProduct.id;
        }

        if (detail.productId && String(detail.productId).split('/').pop() !== this.dataset.productId) {
          return;
        }
        if (isMainStory) {
          const variantSku = resource?.sku || '';
          this.refs.sku.textContent = variantSku;
          const row = this.closest('[data-stowage-sku-row]');
          if (row instanceof HTMLElement) row.hidden = !variantSku;
          return;
        }
        if (resource) {
          const variantSku = resource.sku || '';

          if (variantSku) {
            // Show the component and update the SKU
            this.style.display = 'block';
            this.refs.sku.textContent = variantSku;
          } else {
            // Hide the entire component when SKU is empty
            this.style.display = 'none';
            this.refs.sku.textContent = '';
          }
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-sku] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
